import { getTodayYMD } from "./dateUtils";

const keyForDate = (ymd: string) => `dpReleasedToToday_${ymd}`;

function parseReleasedArray(raw: string | null): Set<number> {
  if (raw === null || raw === "") return new Set();
  try {
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.map((n) => Number(n)).filter((n) => Number.isFinite(n)));
  } catch {
    return new Set();
  }
}

/** Current released IDs for today without creating or mutating storage. */
export function readReleasedOrderIdsForToday(): Set<number> {
  return parseReleasedArray(localStorage.getItem(keyForDate(getTodayYMD())));
}

function orderIdsAlreadyOutForDelivery(
  orders: Array<{ id?: number; status?: string }>
): number[] {
  return orders
    .filter((o) => String(o.status ?? "").toLowerCase() === "out_for_delivery")
    .map((o) => Number(o.id))
    .filter((n) => Number.isFinite(n));
}

/**
 * Per-day set of order IDs released from Basket to Today's run.
 * Merges localStorage with any `out_for_delivery` orders from the API — if the
 * server already marks an order picked up, it appears under Today's pending
 * even when Confirm was not tapped in Basket on this device.
 */
export function getReleasedOrderIdsSynced(
  orders: Array<{ id?: number; status?: string }>
): Set<number> {
  const ymd = getTodayYMD();
  const key = keyForDate(ymd);
  const raw = localStorage.getItem(key);

  if (orders.length === 0) {
    return raw !== null && raw !== "" ? parseReleasedArray(raw) : new Set();
  }

  const released =
    raw !== null && raw !== "" ? parseReleasedArray(raw) : new Set<number>();

  const beforeSize = released.size;
  for (const id of orderIdsAlreadyOutForDelivery(orders)) {
    released.add(id);
  }

  if (raw === null || raw === "" || released.size !== beforeSize) {
    localStorage.setItem(key, JSON.stringify([...released]));
  }

  return released;
}

export function addReleasedOrderIds(ids: number[]): void {
  const finite = ids.map(Number).filter((n) => Number.isFinite(n));
  if (finite.length === 0) return;
  const cur = readReleasedOrderIdsForToday();
  finite.forEach((id) => cur.add(id));
  localStorage.setItem(keyForDate(getTodayYMD()), JSON.stringify([...cur]));
}
